'use client'

import Link from 'next/link'
import { ImageWithFallback } from '@/components/ImageWithFallback'
import { IndustryTag } from './IndustryTag'
import { TechStackTag } from './TechStackTag'
import type { Team } from '@/lib/api/teams'

interface TeamCardProps {
  data: Team
}

export function TeamCard({ data }: TeamCardProps) {
  // 获取行业标签
  const industryTags = data.industries?.map(i => i.industry_tag_string).filter((i): i is string => !!i) || []

  // 获取技能标签
  const techStack = data.skills?.map(s => s.skill_tag_string).filter((s): s is string => !!s) || []

  // 团队名称
  const teamName = data.nickname || '未命名团队'

  // 城市和团队规模
  const city = data.basic_info?.city_string || ''
  const teamSize = data.basic_info?.team_size || ''

  return (
    <Link
      href={`/team/${data.id}`}
      className="block group"
    >
      <article className="bg-white rounded-lg overflow-hidden border border-gray-100 transition-all duration-200 hover:shadow-md hover:scale-[1.02]">
        {/* Team Avatar */}
        <div className="aspect-square bg-gray-100 overflow-hidden">
          <ImageWithFallback
            src={data.avatar}
            alt={teamName}
            fallbackText={teamName}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>

        {/* Content Area */}
        <div className="p-4">
          {/* Team Name */}
          <h2 className="text-lg font-medium text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
            {teamName}
          </h2>

          {/* Team Info - City and Size */}
          {(city || teamSize) && (
            <div className="text-sm text-gray-500 mb-3">
              {city && <span>{city}</span>}
              {city && teamSize && <span className="mx-1">·</span>}
              {teamSize && <span>{teamSize}</span>}
            </div>
          )}

          {/* Tags Row - Industry and Skills */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Industry Tags */}
            {industryTags.filter(Boolean).slice(0, 3).map((tag) => (
              <IndustryTag key={`${data.id}-industry-${tag}`} tag={tag} />
            ))}

            {/* Tech Stack Tags */}
            <div className="flex flex-wrap gap-1">
              {techStack.filter(Boolean).slice(0, 6).map((tech) => (
                <TechStackTag key={`${data.id}-tech-${tech}`} tech={tech} />
              ))}
            </div>
          </div>
        </div>
      </article>
    </Link>
  )
}
